// ============================================================================
//  GIGATT APEX — brand tokens
// ============================================================================
//
//  Single source of truth for the MEFIMS look: the deep navy / apex green
//  pairing from the Gigatt Apex marketing site. Setup, login and the
//  first-login prompt all import from here so a colour change lands in one
//  place instead of being retyped as a hex literal in five components.
//
//  Anything that needs to be overridable per deployment is ALSO stored in
//  `system_settings` under the `brand.*` keys (migration 027). GET
//  /setup/status returns those; `mergeBranding()` below folds them over the
//  defaults so a white-labelled install can re-skin without a rebuild.

export const BRAND = {
  // ── Core palette ──────────────────────────────────────────────────────────
  navy:        '#123A6D',   // primary — headings, nav band, primary buttons
  navyDark:    '#0B2452',   // gradient end / darkest surface
  navyLight:   '#1D4E8F',   // hovers and lighter navy fills
  navySoft:    '#E8EEF7',   // tinted background for navy-keyed surfaces

  green:       '#2E9E4F',   // accent — CTAs, success, active step
  greenDark:   '#217A3C',
  greenLight:  '#3FBF64',
  greenSoft:   '#E7F5EC',

  // ── Neutrals ──────────────────────────────────────────────────────────────
  ink:         '#0F1F3D',   // primary text
  inkSoft:     '#42536E',   // body text that is not a heading — see below
  inkMuted:    '#5A6B87',   // secondary text
  inkFaint:    '#93A2B8',   // captions, placeholders
  line:        '#DDE5EF',   // borders and dividers
  surface:     '#FFFFFF',
  canvas:      '#F4F7FB',   // page background

  // ── Status ────────────────────────────────────────────────────────────────
  danger:      '#D64545',
  dangerSoft:  '#FDECEC',   // tinted background behind a danger message
  warning:     '#E08B2F',
  info:        '#2B7FC4',
};

//  `inkSoft` and `dangerSoft` were being READ before they were ever defined.
//  ClaimsMenu.js alone referenced BRAND.inkSoft 26 times and BRAND.dangerSoft
//  twice, and every one of them evaluated to `undefined` — React drops a style
//  property whose value is undefined, so those elements silently fell back to
//  whatever colour they happened to inherit. Nothing errored; the text just
//  never got the colour it was written to have, which is why the Advanced
//  Filter's checkbox labels read as washed out.
//
//  inkSoft sits between `ink` and `inkMuted`: darker than secondary text
//  because it is used for CONTENT (option labels, table values), not captions.

// ── Product copy, lifted from the Gigatt Apex site ──────────────────────────
export const PRODUCT = {
  vendor:        'GIGATT APEX',
  vendorTagline: 'Innovating Healthcare Solutions',
  name:          'MEFIMS',
  fullName:      'Medical Facilities Information Management System',
  blurb:
    'A comprehensive, integrated solution that digitizes hospital operations ' +
    'and streamlines NHIS claims for faster reimbursements and better ' +
    'healthcare delivery.',
};

// The capability strip that runs under the hero on the marketing site.
// Reused on the setup and login brand panels so the product reads the same
// everywhere a buyer sees it.
export const PILLARS = [
  { title: 'End-to-End',     sub: 'Hospital Management' },
  { title: 'NHIS Claims',    sub: 'Control & Tracking' },
  { title: 'Real-time',      sub: 'Analytics & Reports' },
  { title: 'Secure Cloud',   sub: 'Based System' },
  { title: 'Improved',       sub: 'Efficiency & Revenue' },
];

// The outcomes band: "Driving Better Outcomes Across Healthcare Facilities".
//
// These describe what the SYSTEM delivers for the facility in front of it.
// An install-base count ("200+ facilities") was deliberately removed — it says
// something about the vendor, not about the hospital setting the system up,
// and it is the kind of figure that quietly goes stale.
export const OUTCOMES = [
  { value: '98%',  label: 'Claim Accuracy' },
  { value: '40%',  label: 'Faster Reimbursements' },
  { value: '30%',  label: 'Increase in Revenue' },
  { value: '24/7', label: 'System Availability' },
];

// Narration shown above the outcome figures. Explains what the operator is
// actually installing, in the order a hospital experiences it — front desk,
// clinical floor, then the money. Kept to three lines: this is a setup
// screen, not a brochure.
export const NARRATION = [
  {
    title: 'One record, from reception to discharge',
    body:
      'Registration, vitals, consultation, pharmacy, laboratory and billing ' +
      'write to a single patient record — no re-keying between departments.',
  },
  {
    title: 'Claims prepared as care is delivered',
    body:
      'Diagnoses, procedures and medications are coded at the point of entry, ' +
      'so NHIS claims are assembled continuously rather than rebuilt at month end.',
  },
  {
    title: 'Accountable by design',
    body:
      'Every clinical entry, payment and reversal is attributed to a named user ' +
      'and time-stamped, giving management a defensible audit trail.',
  },
];

// ── Reusable style fragments ────────────────────────────────────────────────

export const GRADIENT_NAVY  = `linear-gradient(160deg, ${BRAND.navy} 0%, ${BRAND.navyDark} 100%)`;
export const GRADIENT_GREEN = `linear-gradient(135deg, ${BRAND.greenLight} 0%, ${BRAND.green} 55%, ${BRAND.greenDark} 100%)`;

export const FONT_STACK = '"Inter","Segoe UI","Helvetica Neue",Arial,sans-serif';

// Outlined MUI input styling used across setup + login so every field on the
// brand-facing screens focuses the same way.
export const brandFieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    background: BRAND.surface,
    transition: 'box-shadow .18s ease, border-color .18s ease',
    '& fieldset':                { borderColor: BRAND.line },
    '&:hover fieldset':          { borderColor: BRAND.navyLight },
    '&.Mui-focused fieldset':    { borderColor: BRAND.navy, borderWidth: '1.5px' },
    '&.Mui-focused':             { boxShadow: `0 0 0 4px ${BRAND.navy}14` },
    '&.Mui-error.Mui-focused':   { boxShadow: `0 0 0 4px ${BRAND.danger}1F` },
  },
  '& .MuiInputLabel-root.Mui-focused': { color: BRAND.navy, fontWeight: 600 },
};

// Primary CTA — the green "Request Demo" button from the site.
export const brandButtonSx = {
  background: GRADIENT_GREEN,
  color: '#fff',
  borderRadius: '10px',
  textTransform: 'none',
  fontWeight: 700,
  letterSpacing: '.2px',
  boxShadow: `0 4px 14px ${BRAND.green}42`,
  '&:hover': { background: GRADIENT_GREEN, filter: 'brightness(1.06)', boxShadow: `0 6px 20px ${BRAND.green}55` },
  '&:disabled': { background: '#C7D2E0', color: '#fff', boxShadow: 'none' },
  transition: 'all .22s ease',
};

// Secondary / navy-outlined action.
export const brandGhostButtonSx = {
  borderRadius: '10px',
  textTransform: 'none',
  fontWeight: 600,
  color: BRAND.navy,
  borderColor: BRAND.line,
  '&:hover': { borderColor: BRAND.navy, background: BRAND.navySoft },
};

// ── Runtime override ────────────────────────────────────────────────────────
// Folds the `brand.*` rows returned by GET /setup/status over the defaults.
// Unknown keys are ignored; missing keys keep the default, so a partially
// populated system_settings table can never blank out the UI.
export function mergeBranding(remote) {
  if (!remote || typeof remote !== 'object') return { colors: BRAND, product: PRODUCT };

  const colors = {
    ...BRAND,
    ...(remote.primary      ? { navy:     remote.primary }      : {}),
    ...(remote.primary_dark ? { navyDark: remote.primary_dark } : {}),
    ...(remote.accent       ? { green:    remote.accent }       : {}),
  };

  const product = {
    ...PRODUCT,
    ...(remote.product_name   ? { name:          remote.product_name }   : {}),
    ...(remote.product_full   ? { fullName:      remote.product_full }   : {}),
    ...(remote.vendor         ? { vendor:        remote.vendor }         : {}),
    ...(remote.vendor_tagline ? { vendorTagline: remote.vendor_tagline } : {}),
  };

  return { colors, product };
}
